import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import Swal from "sweetalert2";

const Deposits = () => {
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pending"); // 'pending' | 'success' | 'reject' | 'all'
  const [searchTerm, setSearchTerm] = useState("");
  const [actionLoadingId, setActionLoadingId] = useState(null);

  // Lightbox Modal State
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [rotation, setRotation] = useState(0);

  const backendLocalApiUrl = process.env.REACT_APP_BACKEND_LOCAL_API;
  const backendLiveApiUrl = process.env.REACT_APP_BACKEND_LIVE_API;
  const nodeMode = process.env.NODE_ENV;
  const baseUrl = nodeMode === "development" ? backendLocalApiUrl : backendLiveApiUrl;

  const fetchDeposits = async () => {
    setLoading(true);
    try {
      const access_token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${access_token}` };

      // Request all deposits so admin can switch tabs instantly
      let url = `${baseUrl}api/admin/deposit-requests?status=all&limit=200`;
      
      let res;
      try {
        res = await axios.get(url, { headers });
      } catch (e) {
        // Fallback to legacy endpoint if needed
        res = await axios.get(`${baseUrl}temp/deposit/pending`, { headers });
      }

      if (res && res.data) {
        const list = res.data.data || res.data.deposits || (Array.isArray(res.data) ? res.data : []);
        setDeposits(list);
      }
    } catch (err) {
      console.error("Error fetching deposit requests:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeposits();
  }, []);

  // Format image URL
  const getImageUrl = (item) => {
    if (!item) return "";
    let raw = item.screenshotUrl || item.screenshot || item.front || "";
    if (!raw) return "";
    if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
    let path = raw.replace(/\\/g, "/");
    if (!path.startsWith("/")) path = "/" + path;
    const cleanBase = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
    return cleanBase + path;
  };

  // Format date helper
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });
  };

  // Stats calculation
  const stats = useMemo(() => {
    let pendingCount = 0;
    let pendingSum = 0;
    let approvedCount = 0;
    let approvedSum = 0;
    let rejectedCount = 0;

    deposits.forEach((d) => {
      const st = (d.status || "").toLowerCase();
      const amt = Number(d.amount) || 0;
      if (st === "pending") {
        pendingCount++;
        pendingSum += amt;
      } else if (st === "success") {
        approvedCount++;
        approvedSum += amt;
      } else if (st === "reject") {
        rejectedCount++;
      }
    });

    return {
      pendingCount,
      pendingSum,
      approvedCount,
      approvedSum,
      rejectedCount,
      totalCount: deposits.length
    };
  }, [deposits]);

  // Filtered deposits based on tab and search
  const filteredDeposits = useMemo(() => {
    return deposits.filter((item) => {
      const itemStatus = (item.status || "").toLowerCase();
      if (activeTab !== "all" && itemStatus !== activeTab) {
        return false;
      }

      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      const utr = (item.utr || item.upi_no || "").toLowerCase();
      const userName = (item.user?.Name || "").toLowerCase();
      const userPhone = String(item.user?.Phone || "");
      const userEmail = (item.user?.Email || "").toLowerCase();
      const id = String(item._id || "").toLowerCase();

      return (
        utr.includes(term) ||
        userName.includes(term) ||
        userPhone.includes(term) ||
        userEmail.includes(term) ||
        id.includes(term)
      );
    });
  }, [deposits, activeTab, searchTerm]);

  // Handle Approve / Accept Deposit
  const handleApprove = async (deposit) => {
    const userName = deposit.user?.Name || "User";
    const amount = deposit.amount;
    const utr = deposit.utr || deposit.upi_no || "N/A";

    const confirm = await Swal.fire({
      title: "Approve Deposit?",
      html: `
        <div style="text-align: left; background: #f8fafc; padding: 12px; border-radius: 8px; font-size: 0.95rem;">
          <p style="margin: 4px 0;"><strong>User:</strong> ${userName}</p>
          <p style="margin: 4px 0;"><strong>Amount:</strong> <span style="color: #16a34a; font-weight: 700; font-size: 1.1rem;">₹${amount}</span></p>
          <p style="margin: 4px 0;"><strong>UTR / Ref:</strong> <code>${utr}</code></p>
        </div>
        <p style="margin-top: 12px; color: #475569; font-size: 0.9rem;">
          This will immediately credit <strong>₹${amount}</strong> to the user's wallet balance.
        </p>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#16a34a",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, Approve & Credit",
      cancelButtonText: "Cancel"
    });

    if (!confirm.isConfirmed) return;

    setActionLoadingId(deposit._id);
    const access_token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${access_token}` };

    try {
      let res;
      try {
        res = await axios.patch(
          `${baseUrl}api/admin/deposit-requests/${deposit._id}/accept`,
          {},
          { headers }
        );
      } catch (e1) {
        // Fallback to legacy endpoint if newer route is not yet deployed on server
        res = await axios.patch(
          `${baseUrl}temp/deposite/${deposit._id}`,
          { status: "success" },
          { headers }
        );
      }

      Swal.fire({
        title: "Deposit Approved!",
        text: `₹${amount} has been successfully credited to ${userName}'s wallet.`,
        icon: "success",
        timer: 3000,
        showConfirmButton: false
      });

      // Close modal if open
      if (selectedReceipt?._id === deposit._id) {
        setSelectedReceipt(null);
      }

      // Refresh data
      await fetchDeposits();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Failed to approve deposit";
      Swal.fire({
        title: "Approval Failed",
        text: msg,
        icon: "error"
      });
    } finally {
      setActionLoadingId(null);
    }
  };

  // Handle Reject Deposit
  const handleReject = async (deposit) => {
    const userName = deposit.user?.Name || "User";
    const amount = deposit.amount;

    const { value: reason, isConfirmed } = await Swal.fire({
      title: "Reject Deposit?",
      html: `
        <p style="color: #64748b; font-size: 0.9rem; margin-bottom: 8px;">
          Rejecting request of <strong>₹${amount}</strong> from <strong>${userName}</strong>.
        </p>
      `,
      input: "text",
      inputPlaceholder: "Enter rejection reason (optional, e.g. Invalid UTR / Fake screenshot)",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, Reject Request",
      cancelButtonText: "Cancel"
    });

    if (!isConfirmed) return;

    setActionLoadingId(deposit._id);
    const access_token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${access_token}` };

    try {
      let res;
      try {
        res = await axios.patch(
          `${baseUrl}api/admin/deposit-requests/${deposit._id}/reject`,
          { reason: reason || "Rejected by Admin" },
          { headers }
        );
      } catch (e1) {
        // Fallback to legacy delete endpoint
        res = await axios.delete(
          `${baseUrl}temp/deposit/delete/${deposit._id}`,
          { headers }
        );
      }

      Swal.fire({
        title: "Deposit Rejected",
        text: "The deposit request has been marked as rejected.",
        icon: "info",
        timer: 2500,
        showConfirmButton: false
      });

      if (selectedReceipt?._id === deposit._id) {
        setSelectedReceipt(null);
      }

      await fetchDeposits();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Failed to reject deposit";
      Swal.fire({
        title: "Action Failed",
        text: msg,
        icon: "error"
      });
    } finally {
      setActionLoadingId(null);
    }
  };

  // Open Lightbox
  const openLightbox = (deposit) => {
    setSelectedReceipt(deposit);
    setZoomLevel(1);
    setRotation(0);
  };

  // Copy to clipboard helper
  const copyText = (txt) => {
    if (!txt) return;
    navigator.clipboard.writeText(txt);
    Swal.fire({
      toast: true,
      position: "top-end",
      icon: "success",
      title: "Copied to clipboard!",
      showConfirmButton: false,
      timer: 1500
    });
  };

  return (
    <div className="container-fluid px-0">
      {/* Page Title & Refresh */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
        <div>
          <h3 className="font-weight-bold text-white mb-1" style={{ letterSpacing: "-0.5px" }}>
            Manual UPI & QR Deposit Requests
          </h3>
          <p className="text-muted mb-0" style={{ fontSize: "0.88rem" }}>
            Review user payment receipts, verify 12-digit UTR numbers, and credit balances instantly.
          </p>
        </div>
        <button
          className="btn btn-outline-info btn-sm d-flex align-items-center"
          onClick={fetchDeposits}
          disabled={loading}
          style={{ borderRadius: "8px", padding: "8px 16px" }}
        >
          <i className={`mdi mdi-refresh mr-1 ${loading ? "mdi-spin" : ""}`}></i>
          {loading ? "Refreshing..." : "Refresh Requests"}
        </button>
      </div>

      {/* Summary Stats Cards */}
      <div className="row mb-4">
        <div className="col-xl-3 col-sm-6 mb-3 mb-xl-0">
          <div
            className="card text-white h-100"
            style={{
              background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
              border: "1px solid rgba(245, 158, 11, 0.3)",
              borderRadius: "12px",
              boxShadow: "0 4px 15px rgba(0,0,0,0.2)"
            }}
          >
            <div className="card-body p-3 d-flex align-items-center justify-content-between">
              <div>
                <span className="text-warning font-weight-bold" style={{ fontSize: "0.8rem", textTransform: "uppercase" }}>
                  Pending Requests
                </span>
                <h3 className="font-weight-bold my-1 text-white">{stats.pendingCount}</h3>
                <small className="text-muted">Total: ₹{stats.pendingSum.toLocaleString("en-IN")}</small>
              </div>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "10px",
                  background: "rgba(245, 158, 11, 0.15)",
                  color: "#f59e0b",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1.5rem"
                }}
              >
                ⏳
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-sm-6 mb-3 mb-xl-0">
          <div
            className="card text-white h-100"
            style={{
              background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
              border: "1px solid rgba(16, 185, 129, 0.3)",
              borderRadius: "12px",
              boxShadow: "0 4px 15px rgba(0,0,0,0.2)"
            }}
          >
            <div className="card-body p-3 d-flex align-items-center justify-content-between">
              <div>
                <span className="text-success font-weight-bold" style={{ fontSize: "0.8rem", textTransform: "uppercase" }}>
                  Approved Deposits
                </span>
                <h3 className="font-weight-bold my-1 text-white">{stats.approvedCount}</h3>
                <small className="text-muted">Credited: ₹{stats.approvedSum.toLocaleString("en-IN")}</small>
              </div>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "10px",
                  background: "rgba(16, 185, 129, 0.15)",
                  color: "#10b981",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1.5rem"
                }}
              >
                ✅
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-sm-6 mb-3 mb-xl-0">
          <div
            className="card text-white h-100"
            style={{
              background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
              border: "1px solid rgba(239, 68, 68, 0.3)",
              borderRadius: "12px",
              boxShadow: "0 4px 15px rgba(0,0,0,0.2)"
            }}
          >
            <div className="card-body p-3 d-flex align-items-center justify-content-between">
              <div>
                <span className="text-danger font-weight-bold" style={{ fontSize: "0.8rem", textTransform: "uppercase" }}>
                  Rejected Deposits
                </span>
                <h3 className="font-weight-bold my-1 text-white">{stats.rejectedCount}</h3>
                <small className="text-muted">Declined requests</small>
              </div>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "10px",
                  background: "rgba(239, 68, 68, 0.15)",
                  color: "#ef4444",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1.5rem"
                }}
              >
                ❌
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-sm-6 mb-3 mb-xl-0">
          <div
            className="card text-white h-100"
            style={{
              background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
              border: "1px solid rgba(99, 102, 241, 0.3)",
              borderRadius: "12px",
              boxShadow: "0 4px 15px rgba(0,0,0,0.2)"
            }}
          >
            <div className="card-body p-3 d-flex align-items-center justify-content-between">
              <div>
                <span className="text-info font-weight-bold" style={{ fontSize: "0.8rem", textTransform: "uppercase" }}>
                  Total Requests
                </span>
                <h3 className="font-weight-bold my-1 text-white">{stats.totalCount}</h3>
                <small className="text-muted">All-time submissions</small>
              </div>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "10px",
                  background: "rgba(99, 102, 241, 0.15)",
                  color: "#6366f1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1.5rem"
                }}
              >
                📊
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Table Card */}
      <div
        className="card"
        style={{
          background: "#0f172a",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: "14px",
          boxShadow: "0 10px 30px rgba(0,0,0,0.3)"
        }}
      >
        <div className="card-body p-4">
          {/* Controls Bar: Filter Tabs & Search */}
          <div className="d-flex flex-wrap justify-content-between align-items-center pb-3 border-bottom border-secondary mb-3 gap-3">
            {/* Filter Tabs */}
            <div className="btn-group" role="group">
              <button
                type="button"
                className={`btn btn-sm ${activeTab === "pending" ? "btn-warning text-dark font-weight-bold" : "btn-outline-secondary"}`}
                onClick={() => setActiveTab("pending")}
                style={{ borderRadius: "8px 0 0 8px" }}
              >
                Pending ({stats.pendingCount})
              </button>
              <button
                type="button"
                className={`btn btn-sm ${activeTab === "success" ? "btn-success font-weight-bold" : "btn-outline-secondary"}`}
                onClick={() => setActiveTab("success")}
              >
                Approved ({stats.approvedCount})
              </button>
              <button
                type="button"
                className={`btn btn-sm ${activeTab === "reject" ? "btn-danger font-weight-bold" : "btn-outline-secondary"}`}
                onClick={() => setActiveTab("reject")}
              >
                Rejected ({stats.rejectedCount})
              </button>
              <button
                type="button"
                className={`btn btn-sm ${activeTab === "all" ? "btn-info font-weight-bold" : "btn-outline-secondary"}`}
                onClick={() => setActiveTab("all")}
                style={{ borderRadius: "0 8px 8px 0" }}
              >
                All ({stats.totalCount})
              </button>
            </div>

            {/* Search Input */}
            <div style={{ minWidth: "260px", maxWidth: "400px", flex: 1 }}>
              <div className="input-group input-group-sm">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by UTR, User Name, Phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    backgroundColor: "rgba(255,255,255,0.05)",
                    borderColor: "rgba(255,255,255,0.15)",
                    color: "#fff",
                    borderRadius: "8px 0 0 8px",
                    padding: "8px 12px"
                  }}
                />
                {searchTerm && (
                  <div className="input-group-append">
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => setSearchTerm("")}
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Table Container */}
          <div className="table-responsive">
            <table className="table table-dark table-hover mb-0" style={{ backgroundColor: "transparent" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid rgba(255,255,255,0.1)", color: "#94a3b8", fontSize: "0.82rem", textTransform: "uppercase" }}>
                  <th style={{ width: "50px" }}>#</th>
                  <th>Request ID</th>
                  <th>User Details</th>
                  <th>Amount</th>
                  <th>UTR / Ref Number</th>
                  <th>Receipt</th>
                  <th>Date & Time</th>
                  <th>Status</th>
                  <th style={{ textAlign: "right", minWidth: "170px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" className="text-center py-5">
                      <div className="spinner-border text-info mb-2" role="status"></div>
                      <p className="text-muted mb-0">Loading deposit requests...</p>
                    </td>
                  </tr>
                ) : filteredDeposits.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center py-5">
                      <div style={{ fontSize: "2.5rem" }}>📭</div>
                      <h5 className="text-white mt-2">No Deposit Requests Found</h5>
                      <p className="text-muted mb-0">
                        {searchTerm
                          ? "No records matched your search query."
                          : `There are currently no ${activeTab} deposit requests.`}
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredDeposits.map((item, index) => {
                    const statusLower = (item.status || "").toLowerCase();
                    const isPending = statusLower === "pending";
                    const isApproved = statusLower === "success";
                    const isRejected = statusLower === "reject";
                    const isProcessing = actionLoadingId === item._id;
                    const imageUrl = getImageUrl(item);
                    const utr = item.utr || item.upi_no || "N/A";

                    return (
                      <tr
                        key={item._id}
                        style={{
                          borderBottom: "1px solid rgba(255,255,255,0.05)",
                          verticalAlign: "middle"
                        }}
                      >
                        {/* Index */}
                        <td className="text-muted font-weight-bold">{index + 1}</td>

                        {/* Request ID */}
                        <td>
                          <div
                            style={{
                              fontFamily: "monospace",
                              fontSize: "0.8rem",
                              color: "#38bdf8",
                              cursor: "pointer"
                            }}
                            title="Click to copy full ID"
                            onClick={() => copyText(item._id)}
                          >
                            #{String(item._id).slice(-8)}
                            <i className="mdi mdi-content-copy ml-1 text-muted" style={{ fontSize: "0.75rem" }}></i>
                          </div>
                        </td>

                        {/* User Details */}
                        <td>
                          <div className="d-flex align-items-center">
                            <div
                              style={{
                                width: "36px",
                                height: "36px",
                                borderRadius: "50%",
                                background: "linear-gradient(135deg, #6366f1, #d946ef)",
                                color: "#fff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: "bold",
                                fontSize: "0.9rem",
                                marginRight: "10px",
                                flexShrink: 0
                              }}
                            >
                              {(item.user?.Name || "U").charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div className="font-weight-bold text-white" style={{ fontSize: "0.9rem" }}>
                                {item.user?.Name || "Unknown User"}
                              </div>
                              <div className="text-muted" style={{ fontSize: "0.78rem" }}>
                                📞 {item.user?.Phone || "No Phone"}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Amount */}
                        <td>
                          <span
                            style={{
                              display: "inline-block",
                              padding: "4px 10px",
                              borderRadius: "6px",
                              background: "rgba(34, 197, 94, 0.15)",
                              color: "#22c55e",
                              fontWeight: "700",
                              fontSize: "1rem"
                            }}
                          >
                            ₹{Number(item.amount).toLocaleString("en-IN")}
                          </span>
                        </td>

                        {/* UTR / Ref Number */}
                        <td>
                          <div
                            className="d-flex align-items-center"
                            style={{
                              background: "rgba(255,255,255,0.04)",
                              padding: "4px 8px",
                              borderRadius: "6px",
                              width: "fit-content",
                              border: "1px solid rgba(255,255,255,0.08)"
                            }}
                          >
                            <span
                              style={{
                                fontFamily: "monospace",
                                fontWeight: "600",
                                color: "#f8fafc",
                                letterSpacing: "0.5px"
                              }}
                            >
                              {utr}
                            </span>
                            <button
                              type="button"
                              onClick={() => copyText(utr)}
                              className="btn btn-link p-0 ml-2 text-info"
                              title="Copy UTR"
                              style={{ lineHeight: 1 }}
                            >
                              <i className="mdi mdi-content-copy"></i>
                            </button>
                          </div>
                        </td>

                        {/* Receipt Thumbnail */}
                        <td>
                          {imageUrl ? (
                            <div
                              onClick={() => openLightbox(item)}
                              style={{
                                position: "relative",
                                width: "48px",
                                height: "48px",
                                borderRadius: "8px",
                                overflow: "hidden",
                                cursor: "pointer",
                                border: "1.5px solid rgba(56, 189, 248, 0.4)",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.3)"
                              }}
                              title="Click to view full screenshot"
                            >
                              <img
                                src={imageUrl}
                                alt="Receipt"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  transition: "transform 0.2s"
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.15)")}
                                onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                              />
                              <div
                                style={{
                                  position: "absolute",
                                  inset: 0,
                                  background: "rgba(0,0,0,0.3)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  opacity: 0,
                                  transition: "opacity 0.2s"
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
                                onMouseLeave={(e) => (e.currentTarget.style.opacity = "0")}
                              >
                                <i className="mdi mdi-magnify-plus text-white" style={{ fontSize: "1.2rem" }}></i>
                              </div>
                            </div>
                          ) : (
                            <span className="text-muted" style={{ fontSize: "0.8rem" }}>No Image</span>
                          )}
                        </td>

                        {/* Date & Time */}
                        <td>
                          <div style={{ fontSize: "0.8rem", color: "#cbd5e1" }}>
                            {formatDate(item.createdAt)}
                          </div>
                        </td>

                        {/* Status Badge */}
                        <td>
                          {isPending && (
                            <span className="badge badge-warning text-dark font-weight-bold px-2 py-1">
                              Pending
                            </span>
                          )}
                          {isApproved && (
                            <span className="badge badge-success font-weight-bold px-2 py-1">
                              Approved ✓
                            </span>
                          )}
                          {isRejected && (
                            <span className="badge badge-danger font-weight-bold px-2 py-1">
                              Rejected ✕
                            </span>
                          )}
                        </td>

                        {/* Actions */}
                        <td style={{ textAlign: "right" }}>
                          {isPending ? (
                            <div className="btn-group" role="group">
                              <button
                                className="btn btn-success btn-sm font-weight-bold mr-1"
                                onClick={() => handleApprove(item)}
                                disabled={isProcessing}
                                style={{ borderRadius: "6px", padding: "6px 12px" }}
                              >
                                {isProcessing ? (
                                  <span className="spinner-border spinner-border-sm"></span>
                                ) : (
                                  <>✓ Accept</>
                                )}
                              </button>
                              <button
                                className="btn btn-danger btn-sm font-weight-bold"
                                onClick={() => handleReject(item)}
                                disabled={isProcessing}
                                style={{ borderRadius: "6px", padding: "6px 12px" }}
                              >
                                ✕ Reject
                              </button>
                            </div>
                          ) : isApproved ? (
                            <span className="text-success font-weight-bold" style={{ fontSize: "0.85rem" }}>
                              Credited ✓
                            </span>
                          ) : (
                            <span className="text-danger font-weight-bold" style={{ fontSize: "0.85rem" }}>
                              Declined ✕
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Lightbox / Modal Modal for Receipt Inspection */}
      {selectedReceipt && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.88)",
            backdropFilter: "blur(8px)",
            zIndex: 999999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px"
          }}
          onClick={() => setSelectedReceipt(null)}
        >
          <div
            style={{
              backgroundColor: "#1e293b",
              borderRadius: "16px",
              maxWidth: "900px",
              width: "100%",
              maxHeight: "92vh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              border: "1px solid rgba(255,255,255,0.15)",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.7)"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              className="p-3 d-flex justify-content-between align-items-center"
              style={{ borderBottom: "1px solid rgba(255,255,255,0.1)", background: "#0f172a" }}
            >
              <div>
                <h5 className="text-white font-weight-bold mb-0">
                  Payment Receipt Inspection
                </h5>
                <small className="text-muted">
                  Request #{String(selectedReceipt._id).slice(-8)} • Submitted by {selectedReceipt.user?.Name}
                </small>
              </div>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setSelectedReceipt(null)}
                style={{ borderRadius: "50%", width: "32px", height: "32px", padding: 0 }}
              >
                ✕
              </button>
            </div>

            {/* Modal Body: Image & Details */}
            <div className="row no-gutters flex-grow-1" style={{ overflow: "hidden", minHeight: "380px" }}>
              {/* Image Preview Canvas */}
              <div
                className="col-md-7 d-flex align-items-center justify-content-center p-3"
                style={{
                  backgroundColor: "#020617",
                  position: "relative",
                  overflow: "auto"
                }}
              >
                <div style={{ textAlign: "center" }}>
                  <img
                    src={getImageUrl(selectedReceipt)}
                    alt="Payment Screenshot"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "56vh",
                      objectFit: "contain",
                      transform: `scale(${zoomLevel}) rotate(${rotation}deg)`,
                      transition: "transform 0.25s ease",
                      borderRadius: "8px",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.5)"
                    }}
                  />
                </div>

                {/* Image Toolbar Floating */}
                <div
                  style={{
                    position: "absolute",
                    bottom: "12px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    background: "rgba(15, 23, 42, 0.85)",
                    padding: "4px 8px",
                    borderRadius: "20px",
                    display: "flex",
                    gap: "6px",
                    backdropFilter: "blur(6px)",
                    border: "1px solid rgba(255,255,255,0.1)"
                  }}
                >
                  <button
                    className="btn btn-sm btn-dark text-white p-1 px-2"
                    onClick={() => setZoomLevel((z) => Math.min(z + 0.25, 3))}
                    title="Zoom In"
                  >
                    🔍+
                  </button>
                  <button
                    className="btn btn-sm btn-dark text-white p-1 px-2"
                    onClick={() => setZoomLevel((z) => Math.max(z - 0.25, 0.75))}
                    title="Zoom Out"
                  >
                    🔍-
                  </button>
                  <button
                    className="btn btn-sm btn-dark text-white p-1 px-2"
                    onClick={() => setRotation((r) => (r + 90) % 360)}
                    title="Rotate"
                  >
                    ↻ Rotate
                  </button>
                  <button
                    className="btn btn-sm btn-dark text-white p-1 px-2"
                    onClick={() => {
                      setZoomLevel(1);
                      setRotation(0);
                    }}
                    title="Reset"
                  >
                    ↺ Reset
                  </button>
                  <a
                    href={getImageUrl(selectedReceipt)}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-sm btn-info p-1 px-2 text-white"
                    title="Open original in new tab"
                  >
                    ↗ Full Tab
                  </a>
                </div>
              </div>

              {/* Receipt Details Sidebar */}
              <div
                className="col-md-5 p-4 d-flex flex-column justify-content-between"
                style={{ backgroundColor: "#1e293b", overflowY: "auto" }}
              >
                <div>
                  <h6 className="text-warning font-weight-bold mb-3" style={{ textTransform: "uppercase", fontSize: "0.8rem" }}>
                    Transaction Breakdown
                  </h6>

                  <div className="mb-3 p-3 rounded" style={{ backgroundColor: "rgba(255,255,255,0.04)" }}>
                    <span className="text-muted" style={{ fontSize: "0.8rem" }}>Claimed Deposit Amount</span>
                    <h3 className="text-success font-weight-bold my-1">
                      ₹{Number(selectedReceipt.amount).toLocaleString("en-IN")}
                    </h3>
                  </div>

                  <div className="mb-3">
                    <span className="text-muted" style={{ fontSize: "0.8rem" }}>12-Digit UTR / Reference Number</span>
                    <div className="d-flex align-items-center mt-1">
                      <span
                        className="font-weight-bold text-white p-2 rounded flex-grow-1"
                        style={{
                          background: "#0f172a",
                          fontFamily: "monospace",
                          fontSize: "0.95rem",
                          letterSpacing: "1px",
                          border: "1px solid rgba(255,255,255,0.1)"
                        }}
                      >
                        {selectedReceipt.utr || selectedReceipt.upi_no || "N/A"}
                      </span>
                      <button
                        type="button"
                        className="btn btn-info btn-sm ml-2"
                        onClick={() => copyText(selectedReceipt.utr || selectedReceipt.upi_no)}
                      >
                        Copy
                      </button>
                    </div>
                  </div>

                  <div className="mb-3">
                    <span className="text-muted" style={{ fontSize: "0.8rem" }}>User Information</span>
                    <div className="text-white font-weight-bold mt-1">
                      {selectedReceipt.user?.Name}
                    </div>
                    <div className="text-muted" style={{ fontSize: "0.82rem" }}>
                      Phone: {selectedReceipt.user?.Phone || "N/A"}
                    </div>
                    <div className="text-muted" style={{ fontSize: "0.82rem" }}>
                      Current Balance: ₹{selectedReceipt.user?.Wallet_balance || 0}
                    </div>
                  </div>

                  <div className="mb-3">
                    <span className="text-muted" style={{ fontSize: "0.8rem" }}>Submission Timestamp</span>
                    <div className="text-white mt-1" style={{ fontSize: "0.85rem" }}>
                      {formatDate(selectedReceipt.createdAt)}
                    </div>
                  </div>
                </div>

                {/* Modal Footer Actions */}
                <div className="pt-3 border-top border-secondary">
                  {(selectedReceipt.status || "").toLowerCase() === "pending" ? (
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-success font-weight-bold flex-grow-1 mr-2"
                        onClick={() => handleApprove(selectedReceipt)}
                        disabled={actionLoadingId === selectedReceipt._id}
                        style={{ padding: "10px" }}
                      >
                        ✓ Accept & Credit Balance
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger font-weight-bold flex-grow-1"
                        onClick={() => handleReject(selectedReceipt)}
                        disabled={actionLoadingId === selectedReceipt._id}
                        style={{ padding: "10px" }}
                      >
                        ✕ Reject
                      </button>
                    </div>
                  ) : (
                    <div className="text-center p-2 rounded" style={{ background: "rgba(255,255,255,0.05)" }}>
                      <span className="text-muted">Status: </span>
                      <strong className="text-white">
                        {(selectedReceipt.status || "").toUpperCase()}
                      </strong>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Deposits;
